import React, { useContext, useEffect, useState } from "react";
import type { GameState } from "../../../shared/types";

import * as gsf from "core/src/core/game-state-functions.js";
import * as respawn from "core/src/core/respawn.js";
import { wormColors } from "core/src/core/constants.js";
import { flagUrlSquare, isFlagCode } from "../../../shared/flags";
import * as replay from "../../../shared/replay-reconstruction.js";
import styles from "../play.module.scss";
import { useIsCompact } from "../../../shared/useResponsive";
import { HudBoardContext, ScoreStripesContext } from "../../../shared/score-fit";

// Match context, shown at the top of the info rail during play (and between
// rounds, so the arena doesn't shift). On the roomy layout the score target is
// the headline (the scoreboard bars measure toward it) with round and alive
// small beside it; in rounds mode the round count takes the headline instead.
// The compact layout keeps the slim one-line readout.
// The alive count is read from the live gameState on a light interval because the
// parent doesn't re-render every frame.
//
// `renderTime` (number or () => number) puts the HUD in replay mode: the gameState
// is a stripped replay state with no live `.alive` flags, so the alive count is
// reconstructed from the death events up to the current replay time instead.
interface Props {
  gameState: GameState;
  roundNumber: number;
  totalPlayers: number;
  maxScore: number;
  // Rounds-mode match: show the round target on the Round item and hide the
  // score Target (maxScore is a 9999 sentinel in that mode, not a real target).
  maxRounds?: number;
  renderTime?: number | (() => number);
  // A two-player match to a small target (a ranked duel: first to 3 of 5) shows
  // the target as dots, one per possible round, filling left to right in each
  // round winner's colour ("First to 3" reads over them). Needs the roster and
  // the winners so far (round-winners.ts); without them the number stands alone.
  players?: any[];
  winners?: string[] | null;
  // A headline of the caller's own in place of the score target (a Royale round:
  // "Out this round 3").
  headline?: { label: string; value: React.ReactNode };
}

// The most rounds a duel's dots will show (first to 3 = 5 dots).
const DOTS_MAX_TARGET = 5;

function DuelDots({ players, winners, maxScore, compact }: { players: any[]; winners: string[]; maxScore: number; compact: boolean }) {
  const dots = 2 * maxScore - 1;
  const colorOf = (id: string) => { const p = players.find((x) => x.id === id); return p ? wormColors[p.colorId] : null; };
  return (
    <span className={compact ? `${styles.hudDots} ${styles.hudDotsCompact}` : styles.hudDots} aria-label={`${winners.length} of ${dots} rounds played, first to ${maxScore}`}>
      {Array.from({ length: dots }, (_, i) => {
        const color = i < winners.length ? colorOf(winners[i]) : null;
        return <i key={i} className={color ? styles.on : ""} style={color ? ({ "--dot": color } as React.CSSProperties) : undefined} />;
      })}
    </span>
  );
}

export default function GameHud({ gameState, roundNumber, totalPlayers, maxScore, maxRounds, renderTime, players, winners, headline }: Props) {
  // The slim one-line readout: phones, and the top bar of a wide map on a
  // computer (no rail there, see GameLayout).
  const isCompact = useIsCompact();
  const stripes = useContext(ScoreStripesContext);
  const boardOnly = useContext(HudBoardContext);
  const compact = isCompact || stripes;
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 200);
    return () => clearInterval(id);
  }, []);

  let aliveCount;
  const now = renderTime !== undefined ? (typeof renderTime === "function" ? renderTime() : renderTime) : gameState.gameTime;
  if (renderTime !== undefined) {
    aliveCount = replay.getAlivePlayerCount(gameState, now);
  } else {
    aliveCount = gsf.getAlivePlayers(gameState).length;
  }
  // A Respawn round (core respawn.js) is everyone in until its timer: the time
  // left takes the alive count's place.
  const timed = respawn.isRespawnRound(gameState);
  const left = timed ? Math.max(0, Math.ceil(respawn.endsAt(gameState) - Math.max(now, gameState.startPhaseTime))) : 0;
  const clock = `${Math.floor(left / 60)}:${String(left % 60).padStart(2, "0")}`;
  const dots = players && players.length === 2 && !maxRounds && maxScore <= DOTS_MAX_TARGET && winners
    ? <DuelDots players={players} winners={winners} maxScore={maxScore} compact={compact} />
    : null;
  // A duel's scoreboard, "race to 3: power bars" (picked in /debug/duel-board,
  // C3): each player one bar from their end to the middle, cut in the target's
  // count of pieces; each round won lights one in their colour, like a charging
  // meter. Flag and name ride on the bar, a VS between the two.
  const duelBoard = dots && players ? (
    <div className={styles.hudDuelBoard}>
      {players.slice(0, 2).map((p: any, k: number) => {
        const color = wormColors[p.colorId];
        const won = (winners || []).filter((id) => id === p.id).length;
        return (
          <React.Fragment key={p.id}>
            {k === 1 && <span className={styles.hudDuelVs}>VS</span>}
            <span className={`${styles.hudDuelBar} ${k === 1 ? styles.hudDuelBarRight : ""}`} style={{ "--c": color } as React.CSSProperties} aria-label={`${p.name}: ${won} of ${maxScore}`}>
              {Array.from({ length: maxScore }, (_, i) => <i key={i} className={i < won ? styles.on : ""} />)}
              <span className={styles.hudDuelLabel}>
                {isFlagCode(p.flag) ? <img className={styles.hudDuelFlag} src={flagUrlSquare(p.flag)} width={24} height={24} alt="" /> : <i className={styles.hudDuelSwatch} />}
                <b className={styles.hudDuelName}>{p.name}</b>
              </span>
            </span>
          </React.Fragment>
        );
      })}
    </div>
  ) : null;
  // Under a wide map's board (GameLayout): only that board, or nothing.
  if (boardOnly) return duelBoard;
  if (!compact) {
    const small = (
      <div className={styles.hudRailSmall}>
        {!maxRounds && (
          <span className={styles.hudRailStat}>
            <span className={styles.gameHudLabel}>Round</span>
            <span className={styles.hudRailSmallValue}>{roundNumber}</span>
          </span>
        )}
        {timed ? (
          <span className={styles.hudRailStat}>
            <span className={styles.gameHudLabel}>Time</span>
            <span className={styles.hudRailSmallValue}>{clock}</span>
          </span>
        ) : (
          <span className={styles.hudRailStat}>
            <span className={styles.gameHudLabel}>Alive</span>
            <span className={`${styles.hudRailSmallValue} ${styles.hudRailAlive}`}>
              {aliveCount}
              <i>/{totalPlayers}</i>
            </span>
          </span>
        )}
      </div>
    );
    if (dots && players) {
      // A duel on a computer: the five marks ARE the scoreboard. Big, under
      // the target and the small stats, with a key naming the two colours;
      // the rail drops its player rows (play.module.scss, .hud-duel-board).
      return (
        <div className={styles.hudDuel}>
          <div className={styles.hudRail}>
            <div className={styles.hudRailHeadline}>
              <span className={styles.gameHudLabel}>First to</span>
              <span className={styles.hudRailBig}>{maxScore}</span>
            </div>
            {small}
          </div>
          {duelBoard}
        </div>
      );
    }
    return (
      <div className={styles.hudRail}>
        <div className={styles.hudRailHeadline}>
          <span className={styles.gameHudLabel}>{headline ? headline.label : maxRounds ? "Round" : "First to"}</span>
          <span className={styles.hudRailBig}>
            {headline ? headline.value : maxRounds ? (
              <>
                {roundNumber}
                <i>/{maxRounds}</i>
              </>
            ) : (
              maxScore
            )}
          </span>
        </div>
        {small}
      </div>
    );
  }

  return (
    <div className={styles.gameHud}>
      <div className={styles.gameHudItem}>
        <span className={styles.gameHudLabel}>Round</span>
        <span className={styles.gameHudValue}>
          {roundNumber}
          {maxRounds ? <i>/{maxRounds}</i> : null}
        </span>
      </div>
      <div className={styles.gameHudSep} />
      {timed ? (
        <div className={styles.gameHudItem}>
          <span className={styles.gameHudLabel}>Time</span>
          <span className={styles.gameHudValue}>{clock}</span>
        </div>
      ) : (
        <div className={`${styles.gameHudItem} ${styles.gameHudAlive}`}>
          <span className={styles.gameHudLabel}>Alive</span>
          <span className={styles.gameHudValue}>
            {aliveCount}
            <i>/{totalPlayers}</i>
          </span>
        </div>
      )}
      {!maxRounds && (
        <>
          <div className={styles.gameHudSep} />
          <div className={styles.gameHudItem}>
            <span className={styles.gameHudLabel}>{headline ? headline.label : dots ? `First to ${maxScore}` : "Target"}</span>
            {headline ? <span className={styles.gameHudValue}>{headline.value}</span> : dots || <span className={styles.gameHudValue}>{maxScore}</span>}
          </div>
        </>
      )}
    </div>
  );
}
